/**
 * Kae Kente rules, kept apart from the React and the WebGL so the
 * scoring and the difficulty curve can be reasoned about (and tested) on
 * their own.
 */

import { BAND_COLOURS, MAX_BANDS, type BandColour } from "./weave-config";

/** Working-memory span sits around 7±2, so starting at 3 and climbing means
 *  nobody grinds through rounds that are too easy for them, and nobody is
 *  thrown in above their head. */
export const START_SPAN = 3;
export const MIN_SPAN = 3;

/** Clean rounds needed before the strip grows by a band. Two rather than one,
 *  so a lucky guess does not push someone past their span. */
export const ROUNDS_PER_STEP_UP = 2;

/**
 * How long a player gets for each tap during recall.
 *
 * Per TAP rather than per sequence (Ken — recall waited forever): a
 * whole-sequence budget either punishes a nine-band round or lets someone
 * stall on a three-band one. The first tap gets a grace because the player is
 * switching from watching to answering.
 *
 * The budget TIGHTENS with the span (Ken again): generous while the strips
 * are short, squeezing as they grow. Span is the right axis because it only
 * rises when the player earns it, so the clock follows their level rather
 * than the wall clock.
 */
export const RECALL_BASE_MS = 5_000;
/** Taken off the budget per band above the starting span. */
export const RECALL_STEP_MS = 450;
export const RECALL_FIRST_TAP_GRACE_MS = 1_200;

/**
 * The span caps at nine — the board frames nine rows and human span runs out
 * around there — so once it does, the clock is the only screw left to turn.
 *
 * From that point every round takes another slice off the budget, and it
 * keeps going until a tap is quicker than anyone can answer. That is
 * deliberate (Ken, 2026-09-04): a run has no cap, so it has to end by getting
 * impossible rather than by getting boring. Otherwise the leaderboard
 * measures who sat there longest.
 */
export const RECALL_OVERTIME_FROM_ROUND = 13;
export const RECALL_OVERTIME_STEP_MS = 120;
/** The point at which a tap is not humanly answerable. A run reaches here and
 *  finishes; it is a backstop, not a difficulty setting. */
export const RECALL_HARD_FLOOR_MS = 900;

/** Deadline for the tap at `position` (0-based) in round `round` of `span`
 *  bands. */
export function recallBudgetMs(
  position: number,
  span: number,
  round = 1,
): number {
  const above = Math.max(0, span - START_SPAN);
  const overtime =
    Math.max(0, round - RECALL_OVERTIME_FROM_ROUND) * RECALL_OVERTIME_STEP_MS;
  const perTap = Math.max(
    RECALL_HARD_FLOOR_MS,
    RECALL_BASE_MS - above * RECALL_STEP_MS - overtime,
  );
  return position === 0 ? perTap + RECALL_FIRST_TAP_GRACE_MS : perTap;
}

/** A run ends after this many wrong answers. Three keeps a run to roughly a
 *  minute, which is the whole promise. */
export const LIVES = 3;

/**
 * The bar for "that went well". Derived rather than guessed: five clean
 * rounds, which takes a player to a span of five, scores 240. Clear that (or
 * beat your own best) and the results screen praises you instead of taunting
 * you — see components/games/encouragement.
 */
export const GOOD_SCORE = 240;

export const BEST_SCORE_KEY = "@KenteBestScore";

/** A sequence with no back-to-back repeats: two identical bands read as one
 *  thick band, which would make the sequence ambiguous rather than hard. */
export function buildSequence(
  span: number,
  random: () => number = Math.random,
): BandColour[] {
  const out: BandColour[] = [];
  for (let i = 0; i < span; i += 1) {
    const pool =
      i === 0
        ? BAND_COLOURS
        : BAND_COLOURS.filter((colour) => colour.key !== out[i - 1].key);
    out.push(pool[Math.floor(random() * pool.length)]);
  }
  return out;
}

/** Points for finishing a round: longer strips are worth more, and a streak
 *  compounds, so pushing your span beats replaying a comfortable one. */
export function roundScore(span: number, streakBefore: number): number {
  return span * 10 + streakBefore * 5;
}

export function nextSpanAfterWin(span: number, streakAfter: number): number {
  const shouldStepUp = streakAfter > 0 && streakAfter % ROUNDS_PER_STEP_UP === 0;
  return shouldStepUp ? Math.min(MAX_BANDS, span + 1) : span;
}

/** A miss eases the span back rather than holding someone at a length they
 *  have just failed. */
export function nextSpanAfterLoss(span: number): number {
  return Math.max(MIN_SPAN, span - 1);
}

export function readBestScore(): number {
  try {
    const raw = localStorage.getItem(BEST_SCORE_KEY);
    const value = raw ? Number(raw) : 0;
    return Number.isFinite(value) && value > 0 ? value : 0;
  } catch {
    // storage unavailable (private browsing) — the run still works, it just
    // will not be remembered
    return 0;
  }
}

export function writeBestScore(score: number): void {
  try {
    if (score > readBestScore()) {
      localStorage.setItem(BEST_SCORE_KEY, String(score));
    }
  } catch {
    // storage unavailable — nothing to remember it with
  }
}

/**
 * Trash talk for the end of a run (Ken, 2026-09-04 — the health tips read as
 * a lecture at exactly the wrong moment). List from products, with Twi spelled
 * in the Twi alphabet: "deɛ" (not "dier") and "hɔ" both need the open vowels
 * ɛ and ɔ, which is why this file is UTF-8 and the copy is not squeezed into
 * ASCII.
 *
 * Rule for anything added here: aim the joke at the player's PLAY, never at a
 * person's health, weight, illness or family's illness. This sits on a health
 * brand next to a memory game, and a line that reads as a joke about losing
 * your memory lands badly on someone whose mother is actually losing hers.
 */
export const TAUNTS = [
  "Chale, go and sit down!",
  "Massa, try again 😂",
  "You no serious!",
  "Chale, this one deɛ… 💀",
  "Ei! Ei! Ei! 🤣",
  "Chairman, w'akyi gu hɔ!",
  "W'asaaaa!",
];

/**
 * A taunt for the end of a run, at random.
 *
 * Not indexed by a run counter: that counter resets on every page load, so
 * the first run of every visit drew the same line and the list looked like it
 * had one entry in it (Ken, 2026-09-04). `exclude` keeps the previous line
 * from repeating back to back, which is the one pattern a player notices.
 */
export function pickTaunt(
  exclude?: string,
  random: () => number = Math.random,
): string {
  const pool =
    TAUNTS.length > 1 && exclude
      ? TAUNTS.filter((taunt) => taunt !== exclude)
      : TAUNTS;
  return pool[Math.floor(random() * pool.length)];
}
